import React, { useState } from 'react';
import {
 TrophyIcon,
 CheckCircleIcon,
 XCircleIcon,
 ClockIcon,
 CreditCardIcon,
 UserIcon,
 CalendarIcon,
 AcademicCapIcon,
 ArrowDownTrayIcon,
 ArrowPathIcon
} from '@heroicons/react/24/outline';

const CertificateSystemDemo = () => {
 const [selectedDashboard, setSelectedDashboard] = useState('technology');

 const dashboards = [
 { id: 'technology', name: 'Technology User Dashboard', progress: 45, eligible: false },
 { id: 'techbunch', name: 'Tech Bunch User Dashboard', progress: 100, eligible: true },
 { id: 'certified', name: 'Certified User Dashboard', progress: 78, eligible: false },
 { id: 'individual', name: 'Individual Roles Dashboard', progress: 100, eligible: true },
 { id: 'team', name: 'Team Roles Dashboard', progress: 60, eligible: false }
 ];

 const currentDashboard = dashboards.find(d => d.id === selectedDashboard);

 const ProgressRing = ({ percent = 0, size = 80, stroke = 8 }) => {
 const r = (size - stroke) / 2;
 const c = 2 * Math.PI * r;
 const dash = ((100 - percent) / 100) * c;
 
 return (
 <div className="flex items-center gap-3">
 <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
 <circle 
 cx={size/2} 
 cy={size/2} 
 r={r} 
 stroke="#3f3f46" 
 strokeWidth={stroke} 
 fill="none"
 />
 <circle 
 cx={size/2} 
 cy={size/2} 
 r={r} 
 stroke="#fde047" 
 strokeWidth={stroke} 
 fill="none" 
 strokeDasharray={c} 
 strokeDashoffset={dash} 
 strokeLinecap="round" 
 transform={`rotate(-90 ${size/2} ${size/2})`}
 />
 </svg>
 <div>
 <div className="text-2xl font-extrabold text-primary">{percent}%</div>
 <div className="text-xs text-muted">Progress</div>
 </div>
 </div>
 );
 };

 const CertificateSection = ({ dashboard }) => (
 <div className="rounded-2xl border border-secondary/40 bg-[#0b0b0b] p-5">
 <h3 className="text-base font-semibold flex items-center gap-2">
 <TrophyIcon className="h-5 w-5 text-primary" /> Certificate
 </h3>
 <p className="mt-1 text-sm text-muted">
 {dashboard.eligible 
 ?"Unlocked! Download your certificate." 
 :"Complete all tasks to unlock your certificate."
 }
 </p>
 <button
 className={`mt-4 w-full rounded-xl px-4 py-3 font-semibold transition border flex items-center justify-center gap-2 ${
 dashboard.eligible
 ?"bg-primary text-white border-primary hover:bg-primary"
 :"bg-transparent text-primary border-secondary/50 hover:bg-secondary/10"
 }`}
 >
 <TrophyIcon className="h-5 w-5" /> 
 {dashboard.eligible ?"View / Download Certificate" :"View Requirements"}
 </button>
 <div className="mt-4">
 <ProgressRing percent={dashboard.progress} />
 </div>
 </div>
 );

 const CertificatePageDemo = () => (
 <div className="space-y-6">
 {/* Header */}
 <div className="bg-primary text-white py-4 px-6 rounded-t-2xl">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <TrophyIcon className="h-8 w-8" />
 <h1 className="text-2xl font-bold">Certificate Center</h1>
 </div>
 <div className="text-sm font-medium">Demo Mode</div>
 </div>
 </div>

 {/* User Information */}
 <div className="rounded-2xl border border-secondary/40 bg-[#0b0b0b] p-6">
 <h2 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
 <UserIcon className="h-5 w-5" />
 Your Information
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="space-y-3">
 <div>
 <span className="text-sm text-muted">Name:</span>
 <div className="text-white font-medium">John Doe</div>
 </div>
 <div>
 <span className="text-sm text-muted">Role:</span>
 <div className="text-white font-medium">Software Engineer</div>
 </div>
 <div>
 <span className="text-sm text-muted">Course:</span>
 <div className="text-white font-medium">AWS Cloud Architect</div>
 </div>
 </div>
 <div className="space-y-3">
 <div>
 <span className="text-sm text-muted">Experience Level:</span>
 <div className="text-white font-medium">3-5 years</div>
 </div>
 <div>
 <span className="text-sm text-muted">Start Date:</span>
 <div className="text-white font-medium">15-08-2024</div>
 </div>
 <div>
 <span className="text-sm text-muted">End Date:</span>
 <div className="text-white font-medium">15-11-2024</div>
 </div>
 </div>
 </div>
 </div>

 {/* Eligibility Status */}
 <div className="rounded-2xl border border-secondary/40 bg-[#0b0b0b] p-6">
 <h2 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
 {currentDashboard.eligible ? (
 <CheckCircleIcon className="h-5 w-5 text-green-400" />
 ) : (
 <XCircleIcon className="h-5 w-5 text-red-400" />
 )}
 Certificate Eligibility
 </h2>
 
 <div className={`p-4 rounded-lg mb-4 ${
 currentDashboard.eligible 
 ? 'bg-green-500/10 border border-green-500/30' 
 : 'bg-red-500/10 border border-red-500/30'
 }`}>
 <div className={`font-semibold mb-2 ${
 currentDashboard.eligible ? 'text-green-400' : 'text-red-400'
 }`}>
 {currentDashboard.eligible ? '✅ Eligible for Certificate' : '❌ Not Eligible'}
 </div>
 <p className="text-sm text-muted">
 {currentDashboard.eligible 
 ?"Congratulations! You are eligible for a certificate."
 : `Complete requirements: ${currentDashboard.progress}% progress, Payment required`
 }
 </p>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="space-y-3">
 <div className="flex items-center gap-3">
 <div className={`w-3 h-3 rounded-full ${
 currentDashboard.progress >= 100 ? 'bg-green-400' : 'bg-red-400'
 }`}></div>
 <span className="text-sm text-muted">
 Progress: {currentDashboard.progress}%
 </span>
 </div>
 <div className="flex items-center gap-3">
 <div className={`w-3 h-3 rounded-full ${
 currentDashboard.eligible ? 'bg-green-400' : 'bg-red-400'
 }`}></div>
 <span className="text-sm text-muted">
 Payment: {currentDashboard.eligible ? 'Completed' : 'Required'}
 </span>
 </div>
 </div>
 <div className="space-y-3">
 <div className="flex items-center gap-2 text-sm text-muted">
 <ClockIcon className="h-4 w-4" />
 <span>Progress tracking active</span>
 </div>
 <div className="flex items-center gap-2 text-sm text-muted">
 <CreditCardIcon className="h-4 w-4" />
 <span>Payment verification enabled</span>
 </div>
 </div>
 </div>
 </div>

 {/* Certificate Generation */}
 {currentDashboard.eligible && (
 <div className="rounded-2xl border border-secondary/40 bg-[#0b0b0b] p-6">
 <h2 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
 <AcademicCapIcon className="h-5 w-5" />
 Generate Certificate
 </h2>
 
 <div className="space-y-4">
 <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
 <div className="text-green-400 font-semibold mb-2 flex items-center gap-2">
 <CheckCircleIcon className="h-5 w-5" />
 Certificate Generated Successfully!
 </div>
 <p className="text-sm text-muted">
 Your certificate has been generated and is ready for download.
 </p>
 </div>
 
 <div className="flex gap-4">
 <button className="bg-green-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-600 transition-colors flex items-center gap-2">
 <ArrowDownTrayIcon className="h-5 w-5" />
 Download Certificate
 </button>
 
 <button className="bg-gray-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-700 transition-colors flex items-center gap-2">
 <ArrowPathIcon className="h-5 w-5" />
 Regenerate
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );

 return (
 <div className="min-h-screen bg-bgMain text-body p-6">
 <div className="max-w-7xl mx-auto">
 <div className="mb-8">
 <h1 className="text-3xl font-bold text-primary mb-4">
 Certificate System Demo
 </h1>
 <p className="text-muted">
 Interactive demonstration of the certificate system across all 5 dashboards
 </p>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
 {/* Dashboard Selector */}
 <div className="space-y-6">
 <h2 className="text-xl font-semibold text-primary">
 Dashboard Certificate Sections
 </h2>
 
 <div className="space-y-4">
 {dashboards.map((dashboard) => (
 <div key={dashboard.id} className="space-y-2">
 <button
 onClick={() => setSelectedDashboard(dashboard.id)}
 className={`w-full text-left p-3 rounded-lg border transition ${
 selectedDashboard === dashboard.id
 ? 'border-primary bg-primary/10'
 : 'border-gray-700 bg-bgSection hover:border-secondary/50'
 }`}
 >
 <div className="flex items-center justify-between">
 <span className="font-medium">{dashboard.name}</span>
 <div className="flex items-center gap-2">
 <span className="text-sm text-muted">
 {dashboard.progress}% progress
 </span>
 {dashboard.eligible ? (
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 ) : (
 <XCircleIcon className="h-4 w-4 text-red-400" />
 )}
 </div>
 </div>
 </button>
 
 {selectedDashboard === dashboard.id && (
 <CertificateSection dashboard={dashboard} />
 )}
 </div>
 ))}
 </div>
 </div>

 {/* Certificate Page Demo */}
 <div className="space-y-6">
 <h2 className="text-xl font-semibold text-primary">
 Certificate Page ({currentDashboard.name})
 </h2>
 <CertificatePageDemo />
 </div>
 </div>

 {/* Legend */}
 <div className="mt-8 p-6 bg-bgSection rounded-2xl">
 <h3 className="text-lg font-semibold text-primary mb-4">Legend</h3>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div className="flex items-center gap-2">
 <CheckCircleIcon className="h-5 w-5 text-green-400" />
 <span className="text-sm text-muted">Eligible for Certificate</span>
 </div>
 <div className="flex items-center gap-2">
 <XCircleIcon className="h-5 w-5 text-red-400" />
 <span className="text-sm text-muted">Not Eligible</span>
 </div>
 <div className="flex items-center gap-2">
 <TrophyIcon className="h-5 w-5 text-primary" />
 <span className="text-sm text-muted">Certificate Section</span>
 </div>
 </div>
 </div>
 </div>
 </div>
 );
};

export default CertificateSystemDemo;









